import * as opt from "lib/opt";
import * as result from "lib/result";
import * as util from "lib/util";
import { ResolveAsrsByLpn } from "lib/asrs/uploadLayout";
import { UseFormReturn } from "react-hook-form";
import { FormSlice } from "store/slices/formSlice";
import { FileData } from "lib/util";

/**
 * Hook that handles file upload processing, validation, and error handling for containment forms.
 *
 * Processes uploaded files asynchronously, validates their content, updates form state,
 * and surfaces errors on the file field (shown in the upload pane). Manages step progression
 * and error recovery for multi-step upload workflows.
 */
// eslint-disable-next-line no-restricted-syntax, @typescript-eslint/naming-convention
export function useFileUploadHandler({
	form,
	setActiveStep,
	setStepStatus,
	resetStepStatus,
}: {
	form: UseFormReturn<FileData>;
	setActiveStep: React.Dispatch<React.SetStateAction<number>>;
	setStepStatus: (status: any) => void;
	resetStepStatus: () => void;
}) {
	const setLoading = (loading: bool) => {
		form.setValue("loading", loading);
	};

	const handleNewFileAction = async (
		action: ReturnType<typeof FormSlice.actions.newFile>
	) => {
		if (!FormSlice.actions.newFile.match(action)) {
			return;
		}

		const files = action.payload;
		if (files.length === 0) {
			return;
		}

		for (const file of files) {
			try {
				setLoading(true);
				form.setValue("file", opt.Some(file), { shouldDirty: true });

				const uploadContentType = form.getValues("uploadContentType");
				const asrsUpload = Boolean(uploadContentType);
				const previewData = await util.ProcessFile(file, 10, {
					partNumberRequired: form.getValues("partNumberRequired"),
					asrsUpload,
					byLpn: asrsUpload
						? ResolveAsrsByLpn({ uploadContentType })
						: undefined,
				});
				form.setValue("parsed", previewData, { shouldDirty: true });
				form.clearErrors("file");
				resetStepStatus();
			} catch (err) {
				const errorMsg = util.GetMsgFromError(err as Error);
				log.Errorf("error processing file %j", errorMsg);
				form.setError("file", {
					type: "error",
					message: errorMsg,
				});
				form.setValue("file", opt.None(), { shouldDirty: true });
				form.setValue("parsed", [], { shouldDirty: true });
				setStepStatus(result.Err(new Error(errorMsg)));
			} finally {
				setLoading(false);
			}
		}
	};

	return {
		handleNewFileAction,
	};
}
